import type {
  DocumentPreview, KycSession, KycStatus, Locale, OnboardingState,
  PlaceSuggestion, RecordMeta, ApiErrorBody,
} from "./types";
import { reportError } from "./lib/ybug";

const TOKEN_KEY = "kyc.token";

export class ApiError extends Error {
  code: string;
  status: number;
  body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || "Coś poszło nie tak.");
    this.status = status;
    this.code = body.code || "error";
    this.body = body;
  }
}

function tokenKey(recordId: string) {
  return `${TOKEN_KEY}.${recordId}`;
}

export function readToken(recordId: string): string | null {
  try {
    return localStorage.getItem(tokenKey(recordId));
  } catch {
    return null; // prywatne okno / zablokowany storage
  }
}

export function storeToken(recordId: string, token: string): void {
  try {
    localStorage.setItem(tokenKey(recordId), token);
  } catch {
    /* brak storage nie może wywrócić procesu */
  }
}

export function forgetToken(recordId: string): void {
  try {
    localStorage.removeItem(tokenKey(recordId));
  } catch {
    /* jw. */
  }
}

async function request<T>(path: string, init: RequestInit = {}, recordId?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const token = recordId ? readToken(recordId) : null;
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    reportError("api", { status: 0, method: init.method ?? "GET", path, code: "offline" });
    throw new ApiError(0, {
      code: "offline",
      message: "Brak połączenia. Sprawdź internet — wypełnione odpowiedzi są zapisane.",
    });
  }

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const payload = parsed as { detail?: ApiErrorBody } | ApiErrorBody | null;
    const body =
      payload && typeof payload === "object" && "detail" in payload && payload.detail
        ? (payload.detail as ApiErrorBody)
        : ((payload as ApiErrorBody) ?? { code: "error", message: `Błąd ${response.status}` });
    reportError("api", { status: response.status, method: init.method ?? "GET", path, code: body.code });
    throw new ApiError(response.status, body);
  }

  return parsed as T;
}

export const api = {
  health: () =>
    request<{
      status: string; dev_mode: boolean; integrations: Record<string, string>;
      ybug?: { id: string | null; environment: string; build: string };
    }>(
      "/api/health",
    ),

  quickstart: () =>
    request<{ record_id: string; magic_link: string }>("/api/dev/quickstart", { method: "POST" }),

  // Język w zapytaniu: teksty marki muszą pójść za przełącznikiem już na bramce,
  // zanim kandydat ma sesję i może zapisać wybór na serwerze.
  meta: (recordId: string, locale?: Locale) =>
    request<RecordMeta>(`/api/o/${recordId}/meta${locale ? `?locale=${locale}` : ""}`),

  sendOtp: (recordId: string, phone: string | null, locale: Locale) =>
    request<{ sent: boolean; phone_hint: string; dev_code: string | null }>(
      `/api/o/${recordId}/otp/send`,
      { method: "POST", body: JSON.stringify({ phone, locale }) },
    ),

  verifyOtp: (recordId: string, code: string) =>
    request<{ token: string; record_id: string }>(`/api/o/${recordId}/otp/verify`, {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  state: (recordId: string, locale: Locale) =>
    request<OnboardingState>(`/api/o/${recordId}/state?locale=${locale}`, {}, recordId),

  answer: (recordId: string, key: string, value: unknown, locale: Locale) =>
    request<OnboardingState>(
      `/api/o/${recordId}/answer`,
      { method: "POST", body: JSON.stringify({ key, value, locale }) },
      recordId,
    ),

  setLocale: (recordId: string, locale: Locale) =>
    request<{ locale: string }>(
      `/api/o/${recordId}/locale`,
      { method: "POST", body: JSON.stringify({ locale }) },
      recordId,
    ),

  help: (recordId: string, comment?: string) =>
    request<{ help_requested: boolean; new: boolean }>(
      `/api/o/${recordId}/help`,
      { method: "POST", body: JSON.stringify({ comment: comment?.trim() || null }) },
      recordId,
    ),

  clear: (recordId: string, key: string, locale: Locale) =>
    request<OnboardingState>(
      `/api/o/${recordId}/clear`,
      { method: "POST", body: JSON.stringify({ key, locale }) },
      recordId,
    ),

  upload: (recordId: string, key: string, file: File, locale: Locale) => {
    const form = new FormData();
    form.append("key", key);
    form.append("locale", locale);
    form.append("file", file);
    return request<OnboardingState>(
      `/api/o/${recordId}/upload`,
      { method: "POST", body: form },
      recordId,
    );
  },

  // Język jedzie w samym żądaniu, bo przełącznik zapisuje wybór osobno i w tle:
  // kto przełączył język i od razu kliknął „rozpocznij", dostawał ekran dostawcy
  // po staremu. Token dostaje język raz, przy tworzeniu sesji.
  startKyc: (recordId: string, key: string, locale: Locale) =>
    request<KycSession>(
      `/api/o/${recordId}/kyc/${key}/start?locale=${locale}`,
      { method: "POST" },
      recordId,
    ),

  kycStatus: (recordId: string, key: string, locale: Locale) =>
    request<KycStatus>(`/api/o/${recordId}/kyc/${key}/status?locale=${locale}`, {}, recordId),

  completeKycMock: (recordId: string, key: string, sessionId: string, document: string) =>
    request<OnboardingState>(
      `/api/o/${recordId}/kyc/${key}/mock-complete`,
      { method: "POST", body: JSON.stringify({ session_id: sessionId, document }) },
      recordId,
    ),

  places: (recordId: string, query: string) =>
    request<{ offline: boolean; items: PlaceSuggestion[] }>(
      `/api/o/${recordId}/places?q=${encodeURIComponent(query)}`,
      {},
      recordId,
    ),

  selectPlace: (recordId: string, key: string, placeId: string, locale: Locale) =>
    request<OnboardingState>(
      `/api/o/${recordId}/places/select`,
      { method: "POST", body: JSON.stringify({ key, place_id: placeId, locale }) },
      recordId,
    ),

  // „Gotowe" dla pytania wieloplikowego — kandydat potwierdza, że wgrał komplet
  // stron. Serwer domyka pytanie dopiero od minimum (patrz complete_files).
  completeFiles: (recordId: string, key: string, locale: Locale) =>
    request<OnboardingState>(
      `/api/o/${recordId}/files/complete`,
      { method: "POST", body: JSON.stringify({ key, locale }) },
      recordId,
    ),

  documentPreview: (recordId: string, key: string, locale: Locale) =>
    request<DocumentPreview>(`/api/o/${recordId}/document/${key}?locale=${locale}`, {}, recordId),

  // PDF dokumentu jako blob, z nagłówkiem autoryzacji. Zwykły <a href> otwierał
  // ten adres nawigacją najwyższego poziomu, która nie niesie tokenu z pamięci —
  // serwer odpowiadał 401 „Potwierdź numer telefonu", i kandydat nie mógł
  // otworzyć tego, co przed chwilą podpisał. `locale` idzie w adresie, żeby
  // dokument wyszedł w języku kandydata, a nie domyślnym języku rekordu.
  documentPdf: async (recordId: string, key: string, locale: Locale): Promise<Blob> => {
    const headers = new Headers();
    const token = readToken(recordId);
    if (token) headers.set("Authorization", `Bearer ${token}`);

    let response: Response;
    try {
      response = await fetch(`/api/o/${recordId}/document/${key}/pdf?locale=${locale}`, { headers });
    } catch {
      throw new ApiError(0, {
        code: "offline",
        message: "Brak połączenia. Sprawdź internet i spróbuj ponownie.",
      });
    }
    if (!response.ok) {
      const raw = await response.text().catch(() => "");
      let parsed: unknown = null;
      try {
        parsed = raw ? JSON.parse(raw) : null;
      } catch {
        parsed = null;
      }
      const payload = parsed as { detail?: ApiErrorBody } | ApiErrorBody | null;
      const body =
        payload && typeof payload === "object" && "detail" in payload && payload.detail
          ? (payload.detail as ApiErrorBody)
          : ((payload as ApiErrorBody) ?? { code: "error", message: `Błąd ${response.status}` });
      reportError("document_pdf", { status: response.status, path: `/api/o/${recordId}/document/${key}/pdf`, code: body.code });
      throw new ApiError(response.status, body);
    }
    return response.blob();
  },

  sign: (recordId: string, key: string, signature: string, locale: Locale) =>
    request<OnboardingState>(
      `/api/o/${recordId}/sign`,
      { method: "POST", body: JSON.stringify({ key, signature, locale }) },
      recordId,
    ),
};
